import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Users, Search, Plus, ChevronRight, X, Mail, Building2, Phone,
  MapPin, CheckCircle2, XCircle, Clock, UserCheck, Trash2, MailWarning, Ban,
  Eye, EyeOff, Key, Send
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'active'
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    send_invite: true
  });
  const [creating, setCreating] = useState(false);
  const [approvingUser, setApprovingUser] = useState(null);
  const [rejectingUser, setRejectingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [visiblePassword, setVisiblePassword] = useState(null);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [sendingPassword, setSendingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let filtered = users.filter(u => 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Apply status filter
    if (statusFilter === 'pending') {
      filtered = filtered.filter(u => u.status === 'pending_approval' || u.status === 'pending_email_verification');
    } else if (statusFilter === 'active') {
      filtered = filtered.filter(u => u.status !== 'pending_approval' && u.status !== 'pending_email_verification');
    }
    
    setFilteredUsers(filtered);
  }, [searchTerm, users, statusFilter]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      // Show all users including admins
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.company_name || !createForm.email) {
      alert('Bitte fülle alle Pflichtfelder aus');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users/invite`, createForm);
      setCreateModalOpen(false);
      setCreateForm({ name: '', company_name: '', email: '', phone: '', send_invite: true });
      fetchUsers();
      alert('Kunde wurde angelegt und erhält eine Einladungs-E-Mail.');
    } catch (error) {
      console.error('Failed to create user:', error);
      alert(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleApproveUser = async (userId, e) => {
    e.stopPropagation(); // Prevent navigation to detail page
    setApprovingUser(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/approve`);
      fetchUsers();
      alert('Benutzer wurde erfolgreich freigeschaltet. Eine Benachrichtigung wurde gesendet.');
    } catch (error) {
      console.error('Failed to approve user:', error);
      alert(error.response?.data?.detail || 'Fehler bei der Freischaltung');
    } finally {
      setApprovingUser(null);
    }
  };

  const handleRejectUser = async (userId, e) => {
    e.stopPropagation();
    if (!window.confirm('Möchtest du diesen Nutzer wirklich ablehnen? Der Nutzer wird per E-Mail benachrichtigt.')) {
      return;
    }
    setRejectingUser(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`);
      fetchUsers();
      alert('Benutzer wurde abgelehnt und per E-Mail benachrichtigt.');
    } catch (error) {
      console.error('Failed to reject user:', error);
      alert(error.response?.data?.detail || 'Fehler beim Ablehnen');
    } finally {
      setRejectingUser(null);
    }
  };

  const handleDeleteUser = async (userId, e) => {
    e.stopPropagation();
    setDeleteConfirmUser(userId);
  };

  const confirmDeleteUser = async () => {
    if (!deleteConfirmUser) return;
    setDeletingUser(deleteConfirmUser);
    try {
      await axios.delete(`${API}/admin/users/${deleteConfirmUser}`);
      setDeleteConfirmUser(null);
      fetchUsers();
      alert('Benutzer und alle zugehörigen Daten wurden gelöscht.');
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeletingUser(null);
    }
  };

  const handleShowPassword = async (userId, e) => {
    e.stopPropagation();
    setPasswordModalUser(userId);
    setVisiblePassword(null);
  };

  const fetchPassword = async (userId) => {
    setLoadingPassword(true);
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/password`);
      setVisiblePassword(response.data.password || response.data.message);
    } catch (error) {
      console.error('Failed to fetch password:', error);
      setVisiblePassword('Fehler beim Abrufen');
    } finally {
      setLoadingPassword(false);
    }
  };

  const sendPasswordEmail = async (userId) => {
    setSendingPassword(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/send-password`);
      alert('Passwort wurde per E-Mail gesendet.');
    } catch (error) {
      console.error('Failed to send password:', error);
      alert(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSendingPassword(false);
    }
  };

  const handleResendVerification = async (userId, e) => {
    e.stopPropagation();
    setResendingEmail(userId);
    try {
      await axios.post(`${API}/admin/users/${userId}/resend-verification`);
      alert('Bestätigungsmail wurde erneut gesendet.');
    } catch (error) {
      console.error('Failed to resend verification:', error);
      alert(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setResendingEmail(null);
    }
  };

  const pendingCount = users.filter(u => u.status === 'pending_approval' || u.status === 'pending_email_verification').length;

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-lilac/50 rounded-none w-1/3"></div>
          <div className="h-64 bg-lilac/50 rounded-none"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-users">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-ink">Kundenverwaltung</h1>
          <p className="text-ink/80 mt-1">
            {users.length} registrierte Kunden
            {pendingCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-none text-sm font-medium">
                {pendingCount} warten auf Freischaltung
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          className="bg-purple hover:bg-deep text-white rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuen Kunden anlegen
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/60" />
          <Input
            type="text"
            placeholder="Suchen nach Name, Unternehmen oder E-Mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 rounded-none"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
            className={statusFilter === 'all' ? 'bg-purple text-white' : ''}
            size="sm"
          >
            Alle
          </Button>
          <Button
            variant={statusFilter === 'pending' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('pending')}
            className={statusFilter === 'pending' ? 'bg-amber-500 text-white hover:bg-amber-600' : ''}
            size="sm"
          >
            <Clock className="w-4 h-4 mr-1" />
            Wartend ({pendingCount})
          </Button>
          <Button
            variant={statusFilter === 'active' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('active')}
            className={statusFilter === 'active' ? 'bg-green-500 text-white hover:bg-green-600' : ''}
            size="sm"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Aktiv
          </Button>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-none border-2 border-ink overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-ink/60">
            {searchTerm ? 'Keine Kunden gefunden' : 'Noch keine Kunden vorhanden'}
          </div>
        ) : (
          <div className="divide-y divide-ink/15">
            {filteredUsers.map((user) => {
              const isPendingApproval = user.status === 'pending_approval';
              const isPendingEmail = user.status === 'pending_email_verification';
              const isPending = isPendingApproval || isPendingEmail;
              
              return (
                <div
                  key={user.id}
                  className={`p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-off transition-colors ${isPending ? 'bg-amber-50/50' : ''}`}
                >
                  <button
                    onClick={() => navigate(`/admin/customers/${user.id}`)}
                    className="flex items-center gap-4 flex-1 text-left min-w-0"
                  >
                    <div className={`w-10 h-10 rounded-none flex items-center justify-center ${isPending ? 'bg-amber-100' : 'bg-purple/10'}`}>
                      <span className={`font-semibold ${isPending ? 'text-amber-700' : 'text-purple'}`}>
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-ink">{user.name}</p>
                        {isPendingEmail && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-none">
                            E-Mail nicht bestätigt
                          </span>
                        )}
                        {isPendingApproval && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-none">
                            Wartet auf Freischaltung
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-ink/60">{user.company_name}</p>
                    </div>
                  </button>
                  <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap sm:justify-end">
                    <div className="sm:text-right hidden sm:block">
                      <p className="text-sm text-ink/80">{user.email}</p>
                      <p className="text-xs text-ink/60">
                        {new Date(user.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    {/* Password Button */}
                    <Button
                      onClick={(e) => handleShowPassword(user.id, e)}
                      variant="ghost"
                      size="sm"
                      className="text-ink/60 hover:text-purple hover:bg-purple/10"
                      title="Passwort anzeigen"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    {isPendingApproval ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          onClick={(e) => handleApproveUser(user.id, e)}
                          disabled={approvingUser === user.id}
                          className="bg-green-500 hover:bg-green-600 text-white"
                          size="sm"
                        >
                          {approvingUser === user.id ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <>
                              <UserCheck className="w-4 h-4 mr-1" />
                              Freischalten
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={(e) => handleRejectUser(user.id, e)}
                          disabled={rejectingUser === user.id}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          size="sm"
                        >
                          {rejectingUser === user.id ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <>
                              <Ban className="w-4 h-4 mr-1" />
                              Ablehnen
                            </>
                          )}
                        </Button>
                      </div>
                    ) : isPendingEmail ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          onClick={(e) => handleResendVerification(user.id, e)}
                          disabled={resendingEmail === user.id}
                          variant="outline"
                          className="border-orange-300 text-orange-600 hover:bg-orange-50"
                          size="sm"
                        >
                          {resendingEmail === user.id ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <>
                              <MailWarning className="w-4 h-4 mr-1" />
                              Mail erneut senden
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={(e) => handleDeleteUser(user.id, e)}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        {user.role === 'erstberatung' && (
                          <span className="px-2 py-1 text-xs font-medium bg-lilac text-ink rounded-none">
                            Erstberatung
                          </span>
                        )}
                        {user.status === 'rejected' && (
                          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-none">
                            Abgelehnt
                          </span>
                        )}
                        {user.status === 'active' && (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        )}
                        <Button
                          onClick={(e) => handleDeleteUser(user.id, e)}
                          variant="ghost"
                          className="text-ink/60 hover:text-red-600 hover:bg-red-50"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <button onClick={() => navigate(`/admin/customers/${user.id}`)}>
                      <ChevronRight className="w-5 h-5 text-ink/60" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Neuen Kunden anlegen</h2>
              <button onClick={() => setCreateModalOpen(false)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-lg text-ink/80 font-medium mb-1.5 block">Name *</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Max Mustermann"
                  className="h-11 rounded-none"
                />
              </div>
              <div>
                <Label className="text-lg text-ink/80 font-medium mb-1.5 block">Unternehmen *</Label>
                <Input
                  value={createForm.company_name}
                  onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })}
                  placeholder="Restaurant Beispiel"
                  className="h-11 rounded-none"
                />
              </div>
              <div>
                <Label className="text-lg text-ink/80 font-medium mb-1.5 block">E-Mail *</Label>
                <Input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="email@beispiel.de"
                  className="h-11 rounded-none"
                />
              </div>
              <div>
                <Label className="text-lg text-ink/80 font-medium mb-1.5 block">Telefon</Label>
                <Input
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  className="h-11 rounded-none"
                />
              </div>
              <p className="text-sm text-ink/60 bg-off p-3 rounded-none">
                Der Kunde erhält automatisch eine E-Mail mit einem Link zur Registrierung.
              </p>
            </div>
            
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setCreateModalOpen(false)} className="flex-1 rounded-none">
                Abbrechen
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={creating}
                className="flex-1"
              >
                {creating ? 'Wird erstellt...' : 'Kunde anlegen & einladen'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink">Kunde löschen</h2>
              <button onClick={() => setDeleteConfirmUser(null)} className="text-ink/60 hover:text-ink/80">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-none flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-ink">
                    {users.find(u => u.id === deleteConfirmUser)?.name}
                  </p>
                  <p className="text-sm text-ink/60">
                    {users.find(u => u.id === deleteConfirmUser)?.email}
                  </p>
                </div>
              </div>
              <p className="text-ink/80 bg-red-50 p-4 rounded-none border border-red-100">
                <strong>Achtung:</strong> Dieser Vorgang löscht den Kunden und alle zugehörigen Daten 
                (Standorte, Systeme, Service-Abos, Tickets) unwiderruflich. Der Kunde wird 
                <strong> nicht</strong> per E-Mail benachrichtigt.
              </p>
            </div>
            
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button variant="outline" onClick={() => setDeleteConfirmUser(null)} className="flex-1 rounded-none">
                Abbrechen
              </Button>
              <Button
                onClick={confirmDeleteUser}
                disabled={deletingUser}
                className="btn--danger flex-1"
              >
                {deletingUser ? 'Wird gelöscht...' : 'Endgültig löschen'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {passwordModalUser && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-none border-2 border-ink shadow-ink-lg max-w-md w-full">
            <div className="p-6 border-b border-ink/15 flex items-center justify-between">
              <h2 className="text-xl font-bold text-ink flex items-center gap-2">
                <Key className="w-5 h-5 text-purple" />
                Passwort
              </h2>
              <button 
                onClick={() => {
                  setPasswordModalUser(null);
                  setVisiblePassword(null);
                }} 
                className="text-ink/60 hover:text-ink/80"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-ink/60 mb-1">Benutzer</p>
                <p className="font-medium text-ink">
                  {users.find(u => u.id === passwordModalUser)?.name}
                </p>
                <p className="text-sm text-ink/60">
                  {users.find(u => u.id === passwordModalUser)?.email}
                </p>
              </div>

              <div className="bg-off rounded-none p-4 mb-4">
                <p className="text-sm text-ink/60 mb-2">Passwort</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-white border-2 border-ink rounded-none px-4 py-3 font-mono text-lg">
                    {visiblePassword === null ? (
                      <span className="text-ink/60">••••••••</span>
                    ) : (
                      <span className="text-ink">{visiblePassword}</span>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      if (visiblePassword === null) {
                        fetchPassword(passwordModalUser);
                      } else {
                        setVisiblePassword(null);
                      }
                    }}
                    variant="outline"
                    className="shrink-0"
                    disabled={loadingPassword}
                  >
                    {loadingPassword ? (
                      <span className="animate-spin">⏳</span>
                    ) : visiblePassword === null ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>

              <p className="text-sm text-ink/60 bg-amber-50 p-3 rounded-none border border-amber-200 mb-4">
                <strong>Hinweis:</strong> Das Passwort wird nur für Nutzer angezeigt, die sich nach der Aktivierung dieser Funktion registriert haben.
              </p>
            </div>
            
            <div className="p-6 border-t border-ink/15 flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setPasswordModalUser(null);
                  setVisiblePassword(null);
                }}
                className="flex-1 rounded-none"
              >
                Schließen
              </Button>
              <Button
                onClick={() => sendPasswordEmail(passwordModalUser)}
                disabled={sendingPassword}
                className="flex-1 bg-purple hover:bg-deep text-white rounded-none"
              >
                {sendingPassword ? (
                  'Wird gesendet...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Per E-Mail senden
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
